/**
 * @fileoverview Resources Results Searcher Module.
 * This module sets up and initializes the Algolia InstantSearch for the resource search results page.
 * It configures search parameters, custom rendering of search hits, and pagination.
 */

import { elementExists, getFullIndexName } from '../Utils';
import instantsearch from 'instantsearch.js';
import { configure, searchBox } from 'instantsearch.js/es/widgets';
import algoliasearch from 'algoliasearch';
import { connectHits } from "instantsearch.js/es/connectors";
import customPagination from './widgets/CustomPagination';
import ResourceHits from "./widgets/ResourceHits";
import { gtmSendSearchData, updateResultCounter, urlUpdateHandler } from "./Utils";
import { debounceEvent } from "../Utils/debounceEvent";

/**
 * @constant
 * @type {object}
 * @description Algolia search client instance configured with application ID and search-only API key.
 */
const searchClient = algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, import.meta.env.VITE_ALGOLIA_READ_KEY);

const resourceContainerSelector = '#resource-results-container';
const searchboxSelector = '#searchbox';
const hitsSelector = '#resources_hits';
const indexName = 'resource';
const paginationSelector = '#pagination';
let dataLayerTimeout = null;
const fullIndexName = getFullIndexName(indexName);

/**
 * Initializes the resource results searcher module.
 * Checks if the resource container exists and initializes the searcher if it does.
 */
const init = () => {
    if (!elementExists(resourceContainerSelector)) return;
    setListeners();
    initSearcher();
};

const setListeners = () => {
    document.addEventListener('urlUpdateEvent', urlUpdateHandler);
}

/**
 * @constant
 * @type {URLSearchParams}
 * @description URL search parameters to extract the current resource query.
 */
const params = new URLSearchParams(window.location.search);

/**
 * @constant
 * @type {string}
 * @description Current resource query extracted from the URL parameters.
 */
const wbcsdResourceQuery = params.get(`q`);

if (wbcsdResourceQuery) {
    const resultQueryEl = document.getElementById('resource-result-query');
    if (resultQueryEl) {
        resultQueryEl.innerText = wbcsdResourceQuery;
    }
}

/**
 * Initializes and configures the Algolia InstantSearch for the resource results page.
 * Sets up the search parameters, custom widgets, and starts the search process.
 */
const initSearcher = () => {
    const search = instantsearch({
        indexName: fullIndexName,
        searchClient,
        routing: {
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[fullIndexName] || {};
                    let routeState = {};
                    if (indexUiState.query) {
                        routeState.q = indexUiState.query;
                    }
                    routeState.pg = indexUiState.page;

                    debounceEvent('urlUpdateEvent', routeState);
                    return routeState;
                },
                routeToState(routeState) {
                    return {
                        [fullIndexName]: {
                            query: routeState.q || '',
                            page: routeState.pg || 1
                        }
                    };
                }
            }
        },
    });

    /**
     * Custom hits connector for Algolia InstantSearch.
     * Customizes the display of resource hits and updates result counters and tracking data.
     * @type {function}
     */
    const customHits = connectHits((hits) => {
        ResourceHits(hits);
        updateResultCounter(hits);
        dataLayerTimeout = gtmSendSearchData(dataLayerTimeout, hits, 'resources-search-ev');
    });

    search.addWidgets([
        configure({
            hitsPerPage: 12,
        }),
        searchBox({
            container: searchboxSelector,
            placeholder: '',
            showSubmit: true,
            templates: {
                submit: '<div class="absolute left-3 top-6"><svg class="h-[18.28px]" width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
                    '<path d="M12.877 13.7927L17.612 18.5798" stroke="black"/>\n' +
                    '<path d="M13.73 7.13414C13.73 10.6401 10.9202 13.4729 7.46557 13.4729C4.01097 13.4729 1.20117 10.6401 1.20117 7.13414C1.20117 3.62819 4.01097 0.79541 7.46557 0.79541C10.9202 0.79541 13.73 3.62819 13.73 7.13414Z" stroke="#2A2824"/>\n' +
                    '</svg></div>'
            },
        }),
        customHits({
            container: document.querySelector(hitsSelector),
        }),
        customPagination({
            container: document.querySelector(paginationSelector),
        })
    ])
        ;

    search.start();
};

init();